import { keepPreviousData } from "@tanstack/react-query";
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { enUS, es, fr } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import {
  useDataProvider,
  useGetList,
  useLocaleState,
  useNotify,
  useStore,
  useTranslate,
} from "ra-core";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Button, buttonVariants } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

import { CALENDAR_SOURCES, type CalendarSource } from "../common/constants";
import type { CalendarItem } from "../types";
import { useIsMobile } from "@/hooks/use-mobile";

import { dayKey, groupItemsByDay, itemPath } from "./calendarModel";
import { MobileCalendar } from "./MobileCalendar";

import type { CalendarView as View } from "./MobileCalendar";

const ALL_SOURCES = Object.keys(CALENDAR_SOURCES) as CalendarSource[];

const dateFnsLocale = (locale: string) =>
  locale.startsWith("fr") ? fr : locale.startsWith("en") ? enUS : es;

// Where each source stores its date, for drag-to-reschedule.
const RESCHEDULE: Record<
  CalendarSource,
  { resource: string; field: string; timestamp?: boolean }
> = {
  event: { resource: "events", field: "start_at", timestamp: true },
  team_task: { resource: "team_tasks", field: "due_date" },
  task: { resource: "tasks", field: "due_date", timestamp: true },
  deal_closing: { resource: "deals", field: "expected_closing_date" },
  milestone: { resource: "deal_milestones", field: "due_date" },
  renewal: { resource: "subscriptions", field: "next_renewal" },
  invoice_due: { resource: "invoices", field: "due_date" },
};

/**
 * `embedded`: rendered inside the phone's top-bar calendar window
 * (mobile/AgendaWindow) rather than as a page.
 */
export const CalendarPage = ({ embedded = false }: { embedded?: boolean }) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const dfLocale = dateFnsLocale(locale);
  const [view, setView] = useStore<View>("calendar.view", "month");
  const [sources, setSources] = useStore<CalendarSource[]>(
    "calendar.sources",
    ALL_SOURCES,
  );
  const [cursor, setCursor] = useState(() => new Date());
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const isMobile = useIsMobile();

  const range = useMemo(() => {
    if (view === "month") {
      const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
      const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
      return { start, end };
    }
    if (view === "week") {
      return {
        start: startOfWeek(cursor, { weekStartsOn: 1 }),
        end: endOfWeek(cursor, { weekStartsOn: 1 }),
      };
    }
    return { start: cursor, end: addDays(cursor, 60) };
  }, [cursor, view]);

  const { data: items, refetch } = useGetList<CalendarItem>(
    "calendar_items",
    {
      filter: {
        "start_at@gte": format(addDays(range.start, -1), "yyyy-MM-dd"),
        "start_at@lte": format(addDays(range.end, 1), "yyyy-MM-dd"),
      },
      pagination: { page: 1, perPage: 2000 },
      sort: { field: "start_at", order: "ASC" },
    },
    // Keep the previous period on screen while the next one loads (no empty flash on swipe).
    { placeholderData: keepPreviousData },
  );

  const byDay = useMemo(
    () => groupItemsByDay(items ?? [], sources),
    [items, sources],
  );

  const move = (direction: 1 | -1) =>
    setCursor((current) =>
      view === "month"
        ? addMonths(current, direction)
        : view === "week"
          ? addWeeks(current, direction)
          : addDays(current, 30 * direction),
    );

  const reschedule = async (item: CalendarItem, day: string) => {
    const target = RESCHEDULE[item.source];
    if (!target || dayKey(item) === day) return;
    const shift = differenceInCalendarDays(
      new Date(`${day}T00:00:00`),
      new Date(`${dayKey(item)}T00:00:00`),
    );
    try {
      const { data: record } = await dataProvider.getOne(target.resource, {
        id: item.record_id,
      });
      const data: Record<string, unknown> = {};
      if (target.timestamp && record[target.field]) {
        data[target.field] = addDays(
          new Date(record[target.field]),
          shift,
        ).toISOString();
        if (item.source === "event" && record.end_at) {
          data.end_at = addDays(new Date(record.end_at), shift).toISOString();
        }
      } else {
        data[target.field] = day;
      }
      await dataProvider.update(target.resource, {
        id: item.record_id,
        data,
        previousData: record,
      });
      notify("hub.calendar.rescheduled");
      refetch();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    }
  };

  const createEventOn = (day: string) =>
    navigate(
      `/events/create?source=${encodeURIComponent(
        JSON.stringify({ start_at: `${day}T09:00`, all_day: false }),
      )}`,
    );

  const title =
    view === "month"
      ? format(cursor, "LLLL yyyy", { locale: dfLocale })
      : `${format(range.start, "d MMM", { locale: dfLocale })} – ${format(
          range.end,
          // Phones: the year would push the range past the header width.
          isMobile ? "d MMM" : "d MMM yyyy",
          { locale: dfLocale },
        )}`;

  if (isMobile) {
    return (
      <MobileCalendar
        title={title}
        view={view}
        onViewChange={setView}
        cursor={cursor}
        onCursorChange={setCursor}
        onMove={move}
        sources={sources}
        onSourcesChange={setSources}
        byDay={byDay}
        dfLocale={dfLocale}
        onCreate={createEventOn}
        embedded={embedded}
      />
    );
  }

  return (
    <div className="mt-4 flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="mr-2 text-2xl font-semibold first-letter:uppercase">
          {title}
        </h1>
        <Button variant="outline" size="icon" onClick={() => move(-1)}>
          <ChevronLeft className="size-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCursor(new Date())}
        >
          {translate("hub.calendar.today")}
        </Button>
        <Button variant="outline" size="icon" onClick={() => move(1)}>
          <ChevronRight className="size-4" />
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <ToggleGroup
            value={[view]}
            onValueChange={(value: string[]) =>
              value[0] && setView(value[0] as View)
            }
            variant="outline"
            size="sm"
          >
            {(["month", "week", "agenda"] as const).map((v) => (
              <ToggleGroupItem key={v} value={v}>
                {translate(`hub.calendar.${v}`)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <Link to="/events/create" className={buttonVariants({ size: "sm" })}>
            <Plus className="size-4" />
            {translate("hub.calendar.new_event")}
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {ALL_SOURCES.map((source) => {
          const active = sources.includes(source);
          return (
            <button
              key={source}
              type="button"
              onClick={() =>
                setSources(
                  active
                    ? sources.filter((s) => s !== source)
                    : [...sources, source],
                )
              }
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-opacity",
                !active && "opacity-40",
              )}
            >
              <span
                className="size-2 rounded-full"
                style={{ backgroundColor: CALENDAR_SOURCES[source].color }}
              />
              {translate(`hub.calendar.sources.${source}`)}
            </button>
          );
        })}
      </div>

      {view === "agenda" ? (
        <AgendaView byDay={byDay} dfLocale={dfLocale} />
      ) : (
        <GridView
          days={eachDayOfInterval(range)}
          cursor={cursor}
          byDay={byDay}
          dfLocale={dfLocale}
          tall={view === "week"}
          onDrop={reschedule}
          onCreate={createEventOn}
        />
      )}
    </div>
  );
};

const GridView = ({
  days,
  cursor,
  byDay,
  dfLocale,
  tall,
  onDrop,
  onCreate,
}: {
  days: Date[];
  cursor: Date;
  byDay: Map<string, CalendarItem[]>;
  dfLocale: typeof es;
  tall: boolean;
  onDrop: (item: CalendarItem, day: string) => void;
  onCreate: (day: string) => void;
}) => {
  const translate = useTranslate();
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const maxVisible = tall ? 50 : 3;
  const allItems = useMemo(() => [...byDay.values()].flat(), [byDay]);

  return (
    <div className="overflow-hidden rounded-xl border">
      <div className="grid grid-cols-7 border-b bg-muted/50 text-xs font-medium text-muted-foreground">
        {days.slice(0, 7).map((day) => (
          <div key={day.toISOString()} className="px-2 py-1.5 capitalize">
            {format(day, "EEE", { locale: dfLocale })}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const dayItems = byDay.get(key) ?? [];
          const showAll = expanded === key;
          const visible = showAll ? dayItems : dayItems.slice(0, maxVisible);
          return (
            <div
              key={key}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(key);
              }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(null);
                const item = allItems.find(
                  (i) => i.id === e.dataTransfer.getData("text/plain"),
                );
                if (item) onDrop(item, key);
              }}
              onDoubleClick={() => onCreate(key)}
              className={cn(
                "group flex flex-col gap-1 border-b border-r p-1.5",
                tall ? "min-h-[60vh]" : "min-h-28",
                !isSameMonth(day, cursor) && !tall && "bg-muted/30",
                dragOver === key && "bg-primary/10",
              )}
            >
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    "inline-flex size-6 items-center justify-center rounded-full text-xs",
                    isToday(day) &&
                      "bg-primary font-semibold text-primary-foreground",
                    !isSameMonth(day, cursor) &&
                      !tall &&
                      "text-muted-foreground",
                  )}
                >
                  {format(day, "d")}
                </span>
                <button
                  type="button"
                  onClick={() => onCreate(key)}
                  className="opacity-0 transition-opacity group-hover:opacity-100"
                  aria-label={translate("hub.calendar.new_event")}
                >
                  <Plus className="size-3.5 text-muted-foreground" />
                </button>
              </div>
              {visible.map((item) => (
                <CalendarChip key={item.id} item={item} />
              ))}
              {!showAll && dayItems.length > maxVisible && (
                <button
                  type="button"
                  onClick={() => setExpanded(key)}
                  className="text-left text-xs text-muted-foreground hover:underline"
                >
                  {translate("hub.calendar.more", {
                    count: dayItems.length - maxVisible,
                  })}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CalendarChip = ({ item }: { item: CalendarItem }) => {
  const color = CALENDAR_SOURCES[item.source].color;
  const done = item.status === "done";
  return (
    <Link
      to={itemPath(item)}
      draggable
      onDragStart={(e) => e.dataTransfer.setData("text/plain", item.id)}
      title={item.title}
      className={cn(
        "flex items-center gap-1 truncate rounded px-1.5 py-0.5 text-xs hover:brightness-95",
        done && "line-through opacity-60",
      )}
      style={{
        backgroundColor: `${color}22`,
        borderLeft: `3px solid ${color}`,
      }}
    >
      {!item.all_day && (
        <span className="shrink-0 tabular-nums text-muted-foreground">
          {format(new Date(item.start_at), "HH:mm")}
        </span>
      )}
      <span className="truncate">{item.title}</span>
    </Link>
  );
};

const AgendaView = ({
  byDay,
  dfLocale,
}: {
  byDay: Map<string, CalendarItem[]>;
  dfLocale: typeof es;
}) => {
  const translate = useTranslate();
  const days = [...byDay.keys()].sort();
  if (days.length === 0) {
    return (
      <p className="py-12 text-center text-muted-foreground">
        {translate("hub.calendar.no_items")}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {days.map((day) => (
        <div key={day} className="grid gap-2 sm:grid-cols-[160px_1fr]">
          <p
            className={cn(
              "text-sm font-medium capitalize",
              isToday(new Date(`${day}T00:00:00`)) && "text-primary",
            )}
          >
            {format(new Date(`${day}T00:00:00`), "EEEE d MMM", {
              locale: dfLocale,
            })}
          </p>
          <div className="flex flex-col gap-1">
            {byDay.get(day)!.map((item) => (
              <div key={item.id} className="flex items-center gap-2 text-sm">
                <span className="text-xs text-muted-foreground">
                  {translate(`hub.calendar.sources.${item.source}`)}
                </span>
                <div className="min-w-0 flex-1">
                  <CalendarChip item={item} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

CalendarPage.path = "/calendar";
